/**
 * 广告外链归因 — 客户端 capture（通道 B）
 *
 * 页面 hydrate 后以 document.referrer 写入或校正归因 cookie。
 * 由 GlobalComponent → useAttributedEntryCapture 在 mount 时调用，业务方无需手动接入。
 */
import Constants from '@/constants/const';
import { deleteCookie, getCookie, setCookie } from '@/utils/cookie';

import { shouldCaptureAttributedEntry, shouldReconcileReferrerSnapshot } from './capture-logic';
import { ATTRIBUTED_ENTRY_SESSION_COOKIE_OPTIONS } from './constants';
import { hasAttributedEntryData, shouldClearAttributedEntryForSession } from './session-binding';

/** 删除三个归因 cookie（客户端） */
const clearAttributedEntryCookiesClient = (): void => {
  deleteCookie(Constants.ATTRIBUTED_ENTRY_URL);
  deleteCookie(Constants.DOCUMENT_REFERRER_SNAPSHOT);
  deleteCookie(Constants.ATTRIBUTED_ENTRY_BOUND_SESSION);
};

/**
 * 校验 binding 并在 session 不一致时清空归因 cookie。
 * 每次 capture 前调用，与服务端 reconcileAttributedEntrySessionBinding 对齐。
 */
const reconcileAttributedEntrySessionBindingClient = (): void => {
  const currentSession = getCookie(Constants.WEEE_SESSION_TOKEN) ?? '';
  const boundSession = getCookie(Constants.ATTRIBUTED_ENTRY_BOUND_SESSION) ?? '';
  const existingEntryUrl = getCookie(Constants.ATTRIBUTED_ENTRY_URL);
  const existingSnapshot = getCookie(Constants.DOCUMENT_REFERRER_SNAPSHOT);

  if (
    shouldClearAttributedEntryForSession(
      currentSession,
      boundSession,
      hasAttributedEntryData(existingEntryUrl, existingSnapshot)
    )
  ) {
    clearAttributedEntryCookiesClient();
  }
};

/** capture 成功后，将当前 weee_session_token 写入 attributed_entry_bound_session */
const bindAttributedEntryToSessionClient = (): void => {
  const sessionToken = getCookie(Constants.WEEE_SESSION_TOKEN) ?? '';
  setCookie(Constants.ATTRIBUTED_ENTRY_BOUND_SESSION, sessionToken, undefined, ATTRIBUTED_ENTRY_SESSION_COOKIE_OPTIONS);
};

/**
 * 客户端通道 B 入口：hydrate 后以 document.referrer 写入或校正归因 cookie。
 *
 * 调用时机：GlobalComponent mount 时（非 App 内）。
 * 流程：reconcile → 判断是否 capture → 或仅校正 referrer 快照（服务端已写 entry、客户端补 referrer）。
 */
export const captureAttributedEntryOnLanding = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  reconcileAttributedEntrySessionBindingClient();

  const clientReferrer = document.referrer ?? '';
  const pathname = window.location.pathname;
  const existingEntryUrl = getCookie(Constants.ATTRIBUTED_ENTRY_URL);
  const existingSnapshot = getCookie(Constants.DOCUMENT_REFERRER_SNAPSHOT);
  const cookieOptions = ATTRIBUTED_ENTRY_SESSION_COOKIE_OPTIONS;

  if (
    shouldCaptureAttributedEntry({
      referrer: clientReferrer,
      pathname,
      search: window.location.search,
      existingEntryUrl
    })
  ) {
    setCookie(Constants.DOCUMENT_REFERRER_SNAPSHOT, clientReferrer, undefined, cookieOptions);
    setCookie(Constants.ATTRIBUTED_ENTRY_URL, window.location.href, undefined, cookieOptions);
    bindAttributedEntryToSessionClient();
    return;
  }

  // 已有 entry 但 referrer 快照需校正（如服务端先写了 entry，客户端补全 document.referrer）
  if (
    shouldReconcileReferrerSnapshot({
      clientReferrer,
      existingEntryUrl,
      existingReferrerSnapshot: existingSnapshot
    })
  ) {
    setCookie(Constants.DOCUMENT_REFERRER_SNAPSHOT, clientReferrer, undefined, cookieOptions);
  }
};
